import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertCircle, Pencil, X, Phone, Mail, Package,
  FileText, MessageSquare, Plus, Trash2, Camera, ExternalLink, Ticket,
  CheckCircle, Clock, ChevronRight
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEAD_STATUSES = ['Open', 'Received', 'Under Inspection', 'Inspection Complete', 'Quotation Sent', 'Approved'];

const STATUS_STYLES = {
  'Open': 'bg-blue-100 text-blue-700 border-blue-200',
  'Received': 'bg-purple-100 text-purple-700 border-purple-200',
  'Under Inspection': 'bg-amber-100 text-amber-700 border-amber-200',
  'Inspection Complete': 'bg-cyan-100 text-cyan-700 border-cyan-200',
  'Quotation Sent': 'bg-orange-100 text-orange-700 border-orange-200',
  'Approved': 'bg-green-100 text-green-700 border-green-200',
};

const STATUS_DOT = {
  'Open': 'bg-blue-500',
  'Received': 'bg-purple-500',
  'Under Inspection': 'bg-amber-500',
  'Inspection Complete': 'bg-cyan-500',
  'Quotation Sent': 'bg-orange-500',
  'Approved': 'bg-green-500',
};

export const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [lead, setLead] = useState(null);
  const [taxes, setTaxes] = useState([]);
  const [serviceItemsMaster, setServiceItemsMaster] = useState([]);
  const [receivablesMaster, setReceivablesMaster] = useState([]);
  const [serviceUsers, setServiceUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tickets, setTickets] = useState([]);

  // Quotations timeline
  const [quotations, setQuotations] = useState([]);

  // Modals
  const [activeModal, setActiveModal] = useState(null); // 'received' | 'inspection-complete' | 'quotation-sent' | 'approved' | 'remark' | 'image-upload'
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');

  // Received modal form
  const [receivedForm, setReceivedForm] = useState({ issue: '', remark: '', receivables: [], notes: '' });

  // Under Inspection assignee
  const [inspectionAssignee, setInspectionAssignee] = useState('');

  // Inspection Complete assignee (Accounts)
  const [accountsUsers, setAccountsUsers] = useState([]);
  const [inspCompleteAssignee, setInspCompleteAssignee] = useState('');

  // Inspection Complete modal form (service items)
  const [inspectionItems, setInspectionItems] = useState([{ item_id: '', description: '', remarks: '' }]);

  // Quotation Sent modal form (pricing for service items)
  const [pricingItems, setPricingItems] = useState([]);

  // Standalone quotation modal (create new / update last)
  const [qtnModalOpen, setQtnModalOpen] = useState(false);
  const [qtnModalItems, setQtnModalItems] = useState([]);
  const [qtnSubmitting, setQtnSubmitting] = useState(false);
  const [qtnError, setQtnError] = useState('');

  // Remark update
  const [remarkText, setRemarkText] = useState('');

  // Image upload
  const [imageStatus, setImageStatus] = useState('');
  const [imageCaption, setImageCaption] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  // Image blobs for display
  const [imageBlobUrls, setImageBlobUrls] = useState({});

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchLead = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/leads/${id}`, getAuthHeaders());
      setLead(res.data);
    } catch (err) { setError('Failed to load lead'); }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const [leadRes, taxRes, svcRes, rcvRes, usersRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/leads/${id}`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/service-items`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/receivables`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/users`, getAuthHeaders()),
        ]);
        setLead(leadRes.data);
        setTaxes(taxRes.data);
        setServiceItemsMaster(svcRes.data);
        setReceivablesMaster(rcvRes.data);
        setServiceUsers((usersRes.data || []).filter(u => u.active && (u.role_name === 'Service Manager' || u.role_name === 'Service Technician')));
        setAccountsUsers((usersRes.data || []).filter(u => u.active && u.role_name === 'Accounts'));
      } catch (err) { setError('Failed to load lead details'); }
      finally { setLoading(false); }
    };
    init();
  }, [id]);

  useEffect(() => {
    if (lead) {
      axios.get(`${BACKEND_URL}/api/tickets/by-lead/${id}`, getAuthHeaders())
        .then(res => setTickets(res.data)).catch(() => {});
      axios.get(`${BACKEND_URL}/api/quotations/by-lead/${id}`, getAuthHeaders())
        .then(res => setQuotations(res.data)).catch(() => {});
    }
  }, [lead]);

  // Load image blobs
  useEffect(() => {
    if (!lead?.images?.length) return;
    const token = localStorage.getItem('token');
    lead.images.forEach(img => {
      if (!imageBlobUrls[img.id]) {
        axios.get(`${BACKEND_URL}/api/leads/${id}/images/${img.id}?auth=${token}`, { responseType: 'blob' })
          .then(res => {
            const url = URL.createObjectURL(res.data);
            setImageBlobUrls(prev => ({ ...prev, [img.id]: url }));
          }).catch(() => {});
      }
    });
  }, [lead?.images]);

  // Cleanup blob URLs
  useEffect(() => {
    return () => { Object.values(imageBlobUrls).forEach(u => URL.revokeObjectURL(u)); };
  }, []);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const getNextStatus = () => {
    const idx = LEAD_STATUSES.indexOf(lead?.status);
    return idx >= 0 && idx < LEAD_STATUSES.length - 1 ? LEAD_STATUSES[idx + 1] : null;
  };

  const openStatusModal = () => {
    const next = getNextStatus();
    if (!next) return;
    setModalError('');
    if (next === 'Received') {
      const existingRcv = lead.receivables ? lead.receivables.split(',').map(s => s.trim()).filter(Boolean) : [];
      setReceivedForm({ issue: lead.issue || '', remark: lead.remarks || '', receivables: existingRcv, notes: lead.notes || '' });
      setActiveModal('received');
    } else if (next === 'Under Inspection') {
      setInspectionAssignee(lead.assigned_to || '');
      setActiveModal('under-inspection');
    } else if (next === 'Inspection Complete') {
      setInspectionItems(lead.service_items?.length ? lead.service_items.map(si => ({ item_id: si.item_id || '', description: si.description || '', remarks: si.remarks || '' })) : [{ item_id: '', description: '', remarks: '' }]);
      setInspCompleteAssignee('');
      setActiveModal('inspection-complete');
    } else if (next === 'Quotation Sent') {
      setPricingItems((lead.service_items || []).map(si => ({ ...si, cost: si.cost || '', tax_id: si.tax_id || '' })));
      setActiveModal('quotation-sent');
    } else if (next === 'Approved') {
      setActiveModal('approved');
    }
  };

  // Under Inspection - assign and move
  const handleUnderInspection = async (e) => {
    if (e) e.preventDefault();
    if (!inspectionAssignee) { setModalError('Please select an assignee'); return; }
    setModalError(''); setModalSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/leads/${id}/status/under-inspection`, { assigned_to: inspectionAssignee }, getAuthHeaders());
      setLead(res.data); setActiveModal(null);
    } catch (err) { setModalError(err.response?.data?.detail || 'Failed to update status'); }
    finally { setModalSubmitting(false); }
  };

  // Received modal submit
  const handleReceivedSubmit = async (e) => {
    e.preventDefault(); setModalError(''); setModalSubmitting(true);
    try {
      const payload = { ...receivedForm, receivables: receivedForm.receivables.join(', ') };
      const res = await axios.post(`${BACKEND_URL}/api/leads/${id}/status/received`, payload, getAuthHeaders());
      setLead(res.data); setActiveModal(null);
    } catch (err) { setModalError(err.response?.data?.detail || 'Failed'); }
    finally { setModalSubmitting(false); }
  };

  // Inspection Complete submit
  const handleInspectionSubmit = async (e) => {
    e.preventDefault(); setModalError('');
    const valid = inspectionItems.filter(i => i.item_id);
    if (!valid.length) { setModalError('Add at least one service item'); return; }
    if (!inspCompleteAssignee) { setModalError('Please assign to an Accounts user'); return; }
    setModalSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/leads/${id}/status/inspection-complete`, { service_items: valid, assigned_to: inspCompleteAssignee }, getAuthHeaders());
      setLead(res.data); setActiveModal(null);
    } catch (err) { setModalError(err.response?.data?.detail || 'Failed'); }
    finally { setModalSubmitting(false); }
  };

  // Quotation Sent submit
  const handleQuotationSubmit = async (e) => {
    e.preventDefault(); setModalError(''); setModalSubmitting(true);
    try {
      const items = pricingItems.map(pi => ({ ...pi, cost: parseFloat(pi.cost) || 0 }));
      const res = await axios.post(`${BACKEND_URL}/api/leads/${id}/status/quotation-sent`, { service_items: items }, getAuthHeaders());
      setLead(res.data); setActiveModal(null);
    } catch (err) { setModalError(err.response?.data?.detail || 'Failed'); }
    finally { setModalSubmitting(false); }
  };

  // Approved submit
  const handleApprovedSubmit = async () => {
    setModalSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/leads/${id}/status/approved`, {}, getAuthHeaders());
      setLead(res.data); setActiveModal(null);
    } catch (err) { setModalError(err.response?.data?.detail || 'Failed'); }
    finally { setModalSubmitting(false); }
  };

  // Remark update
  const handleRemarkUpdate = async () => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/leads/${id}`, { remarks: remarkText }, getAuthHeaders());
      setLead(res.data); setActiveModal(null);
    } catch (err) {}
  };

  // Image upload
  const handleImageUpload = async () => {
    if (!imageFile) return;
    setImageUploading(true);
    setModalError('');
    try {
      const fd = new FormData();
      fd.append('file', imageFile);
      fd.append('status_at_upload', imageStatus || lead.status || 'Open');
      fd.append('caption', imageCaption);
      const token = localStorage.getItem('token');
      const res = await axios({
        method: 'post',
        url: `${BACKEND_URL}/api/leads/${id}/images`,
        data: fd,
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 120000,
      });
      setLead(res.data); setActiveModal(null); setImageFile(null); setImageCaption(''); setImageStatus('');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Upload failed';
      setModalError(msg);
    }
    finally { setImageUploading(false); }
  };

  const handleDeleteImage = async (imgId) => {
    try {
      const res = await axios.delete(`${BACKEND_URL}/api/leads/${id}/images/${imgId}`, getAuthHeaders());
      setLead(res.data);
    } catch (err) {}
  };

  // Standalone quotation modal helpers
  const openNewQuotation = () => {
    const items = (lead.service_items || []).map(si => ({
      item_id: si.item_id || '', item_name: si.item_name || '', description: si.description || '', cost: '', tax_id: ''
    }));
    setQtnModalItems(items.length ? items : [{ item_id: '', item_name: '', description: '', cost: '', tax_id: '' }]);
    setQtnError(''); setQtnModalOpen(true);
  };

  const openUpdateLastQuotation = () => {
    const last = quotations[0]; // already sorted newest first
    if (!last) return;
    const items = (last.line_items || []).map(li => {
      let itemId = li.item_id || '';
      // Reverse lookup by name if item_id is missing
      if (!itemId && li.service_item) {
        const match = serviceItemsMaster.find(s => s.name === li.service_item);
        if (match) itemId = match.id;
      }
      return {
        item_id: itemId, item_name: li.service_item || '', description: li.description || '',
        cost: li.cost || '', tax_id: li.tax_id || ''
      };
    });
    setQtnModalItems(items.length ? items : [{ item_id: '', item_name: '', description: '', cost: '', tax_id: '' }]);
    setQtnError(''); setQtnModalOpen(true);
  };

  const handleStandaloneQuotationSubmit = async (e) => {
    e.preventDefault(); setQtnError(''); setQtnSubmitting(true);
    try {
      const lineItems = qtnModalItems.map(pi => ({
        item_id: pi.item_id || null,
        service_item: pi.item_name || serviceItemsMaster.find(s => s.id === pi.item_id)?.name || '',
        description: pi.description || '',
        cost: parseFloat(pi.cost) || 0,
        tax_id: pi.tax_id || null,
      }));
      await axios.post(`${BACKEND_URL}/api/quotations`, {
        lead_id: id,
        subject: `Quotation for ${lead.name}`,
        quotation_date: new Date().toISOString().slice(0, 10),
        line_items: lineItems,
        status: 'Open',
      }, getAuthHeaders());
      // Refresh quotations
      const res = await axios.get(`${BACKEND_URL}/api/quotations/by-lead/${id}`, getAuthHeaders());
      setQuotations(res.data);
      setQtnModalOpen(false);
    } catch (err) { setQtnError(err.response?.data?.detail || 'Failed to create quotation'); }
    finally { setQtnSubmitting(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (error && !lead) return (
    <div>
      <button onClick={() => navigate('/admin/services/leads')} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"><ArrowLeft className="h-4 w-4" /> Back to Leads</button>
      <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm"><AlertCircle className="h-5 w-5" />{error}</div>
    </div>
  );
  if (!lead) return null;

  const nextStatus = getNextStatus();
  const statusIdx = LEAD_STATUSES.indexOf(lead.status);

  return (
    <div data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <button onClick={() => navigate('/admin/services/leads')} data-testid="back-btn"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-3 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to Leads
          </button>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">{lead.name}</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => { setRemarkText(lead.remarks || ''); setActiveModal('remark'); }} data-testid="add-remark-btn"
            className="inline-flex items-center gap-2 border border-slate-200 text-slate-700 font-medium px-4 py-2 rounded-sm hover:bg-slate-50 text-sm">
            <MessageSquare className="h-4 w-4" /> Update Remark
          </button>
          <button onClick={() => { setImageStatus(lead.status); setImageCaption(''); setImageFile(null); setModalError(''); setActiveModal('image-upload'); }} data-testid="upload-image-btn"
            className="inline-flex items-center gap-2 border border-slate-200 text-slate-700 font-medium px-4 py-2 rounded-sm hover:bg-slate-50 text-sm">
            <Camera className="h-4 w-4" /> Upload Image
          </button>
          {nextStatus && (
            <button onClick={openStatusModal} data-testid="next-status-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-5 py-2 rounded-none hover:bg-orange-600 transition-colors text-sm">
              <ChevronRight className="h-4 w-4" /> Move to {nextStatus}
            </button>
          )}
        </div>
      </div>

      {/* Status Progress Bar */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-1">
          {LEAD_STATUSES.map((s, i) => (
            <div key={s} className="flex-1 flex items-center">
              <div className={`flex-1 h-2 rounded-full ${i <= statusIdx ? (STATUS_DOT[s] || 'bg-slate-300') : 'bg-slate-200'}`}></div>
              {i < LEAD_STATUSES.length - 1 && <div className="w-1"></div>}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2">
          {LEAD_STATUSES.map((s, i) => (
            <span key={s} className={`text-xs ${i <= statusIdx ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>{s}</span>
          ))}
        </div>
      </div>

      {/* Lead Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-sm border border-slate-200 p-6 lg:col-span-2">
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Lead Information</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div><span className="block text-xs text-slate-500 uppercase mb-0.5">Email</span><span className="text-sm text-slate-900">{lead.email || '-'}</span></div>
            <div><span className="block text-xs text-slate-500 uppercase mb-0.5">Phone</span><span className="text-sm text-slate-900">{lead.phone || '-'}</span></div>
            <div><span className="block text-xs text-slate-500 uppercase mb-0.5">Product</span><span className="text-sm text-slate-900">{lead.product || '-'}</span></div>
            <div className="col-span-2 md:col-span-3"><span className="block text-xs text-slate-500 uppercase mb-0.5">Requirements</span><span className="text-sm text-slate-700 whitespace-pre-wrap">{lead.requirements || '-'}</span></div>
            <div className="col-span-2 md:col-span-3"><span className="block text-xs text-slate-500 uppercase mb-0.5">Remarks</span><span className="text-sm text-slate-700 whitespace-pre-wrap">{lead.remarks || '-'}</span></div>
          </div>
        </div>
        <div className="bg-white rounded-sm border border-slate-200 p-6">
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Service Info</h3>
          <div className="space-y-3">
            <div><span className="block text-xs text-slate-500 uppercase mb-0.5">Status</span>
              <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium border ${STATUS_STYLES[lead.status] || ''}`} data-testid="lead-status-badge">{lead.status}</span>
            </div>
            <div><span className="block text-xs text-slate-500 uppercase mb-0.5">Issue</span><span className="text-sm text-slate-700">{lead.issue || '-'}</span></div>
            <div><span className="block text-xs text-slate-500 uppercase mb-0.5">Receivables</span><span className="text-sm text-slate-700">{lead.receivables || '-'}</span></div>
            <div><span className="block text-xs text-slate-500 uppercase mb-0.5">Notes</span><span className="text-sm text-slate-700">{lead.notes || '-'}</span></div>
            {lead.assigned_to_name && (
              <div><span className="block text-xs text-slate-500 uppercase mb-0.5">Assigned To</span><span className="text-sm font-medium text-orange-600">{lead.assigned_to_name}</span></div>
            )}
          </div>
        </div>
      </div>

      {/* Service Items */}
      {(lead.service_items || []).length > 0 && (
        <div className="bg-white rounded-sm border border-slate-200 mb-6">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Service Items</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                  <th className="px-4 py-2 text-left">#</th>
                  <th className="px-4 py-2 text-left">Item</th>
                  <th className="px-4 py-2 text-left">Description</th>
                  <th className="px-4 py-2 text-left">Remarks</th>
                  {lead.service_items.some(si => si.cost) && <>
                    <th className="px-4 py-2 text-right">Cost</th>
                    <th className="px-4 py-2 text-right">Tax</th>
                    <th className="px-4 py-2 text-right">Total</th>
                  </>}
                </tr>
              </thead>
              <tbody>
                {lead.service_items.map((si, idx) => (
                  <tr key={idx} className="border-b border-slate-100">
                    <td className="px-4 py-2 text-slate-500">{idx + 1}</td>
                    <td className="px-4 py-2 font-medium text-slate-900">{si.item_name || '-'}</td>
                    <td className="px-4 py-2 text-slate-600">{si.description || '-'}</td>
                    <td className="px-4 py-2 text-slate-600">{si.remarks || '-'}</td>
                    {lead.service_items.some(s => s.cost) && <>
                      <td className="px-4 py-2 text-right">{si.cost?.toFixed(2) || '-'}</td>
                      <td className="px-4 py-2 text-right">{si.tax_amount?.toFixed(2) || '-'}</td>
                      <td className="px-4 py-2 text-right font-medium">{si.total?.toFixed(2) || '-'}</td>
                    </>}
                  </tr>
                ))}
                {lead.service_items.some(s => s.cost) && (
                  <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold text-sm">
                    <td className="px-4 py-3" colSpan={4}>
                      <span className="text-slate-700 uppercase tracking-wider text-xs">Totals</span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-900" data-testid="svc-sum-cost">
                      {lead.service_items.reduce((s, si) => s + (si.cost || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600" data-testid="svc-sum-tax">
                      {lead.service_items.reduce((s, si) => s + (si.tax_amount || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-900" data-testid="svc-grand-total">
                      {lead.service_items.reduce((s, si) => s + (si.total || 0), 0).toFixed(2)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Images */}
      {(lead.images || []).length > 0 && (
        <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Images</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {lead.images.map(img => (
              <div key={img.id} className="relative group border border-slate-200 rounded-sm overflow-hidden">
                {imageBlobUrls[img.id] ? (
                  <img src={imageBlobUrls[img.id]} alt={img.caption || img.original_filename} className="w-full h-24 object-cover" />
                ) : (
                  <div className="w-full h-24 bg-slate-100 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
                )}
                <div className="p-2">
                  <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium mb-1 ${STATUS_STYLES[img.status_at_upload]?.split(' ').slice(0, 2).join(' ') || 'bg-slate-100 text-slate-600'}`}>{img.status_at_upload}</span>
                  {img.caption && <p className="text-xs text-slate-500 truncate">{img.caption}</p>}
                </div>
                <button onClick={() => handleDeleteImage(img.id)} data-testid={`delete-img-${img.id}`}
                  className="absolute top-1 right-1 p-1 bg-white/80 rounded text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Linked Tickets */}
      {tickets.length > 0 && (
        <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Tickets</h3>
          {tickets.map(tkt => (
            <div key={tkt.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
              <div className="flex items-center gap-3">
                <Ticket className="h-4 w-4 text-slate-400" />
                <span className="font-mono font-bold text-sm">{tkt.ticket_number}</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[tkt.status]?.split(' ').slice(0, 2).join(' ') || 'bg-slate-100 text-slate-600'}`}>{tkt.status}</span>
              </div>
              <button onClick={() => navigate(`/admin/services/tickets/${tkt.id}`)} className="text-sm text-orange-600 hover:text-orange-700 font-medium inline-flex items-center gap-1">
                View <ExternalLink className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Quotation Timeline & Activity Timeline - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Quotation Timeline */}
        <div className="bg-white rounded-sm border border-slate-200 p-6" data-testid="quotation-timeline">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Quotation Timeline</h3>
            <div className="flex items-center gap-2">
              {quotations.length > 0 && (
                <button onClick={openUpdateLastQuotation} data-testid="update-last-qtn-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 border border-slate-200 px-3 py-1.5 rounded-sm hover:bg-slate-50 transition-colors">
                  <Pencil className="h-3 w-3" /> Update Last
                </button>
              )}
              <button onClick={openNewQuotation} data-testid="create-new-qtn-btn"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 border border-orange-200 px-3 py-1.5 rounded-sm hover:bg-orange-50 transition-colors">
                <Plus className="h-3 w-3" /> New Quotation
              </button>
            </div>
          </div>
          {quotations.length === 0 ? (
            <p className="text-sm text-slate-500">No quotations created yet</p>
          ) : (
            <div className="relative">
              <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-orange-200"></div>
              <div className="space-y-4">
                {quotations.map((qtn, idx) => {
                  const qtnStatusStyles = {
                    'Open': 'bg-blue-100 text-blue-700',
                    'Approved': 'bg-green-100 text-green-700',
                    'Rejected': 'bg-red-100 text-red-700',
                    'Cancelled': 'bg-slate-100 text-slate-600',
                  };
                  return (
                    <div key={qtn.id} className="relative pl-9" data-testid={`qtn-entry-${idx}`}>
                      <div className={`absolute left-1.5 top-1.5 w-3 h-3 rounded-full ${idx === 0 ? 'bg-orange-500' : 'bg-orange-300'}`}></div>
                      <div className="bg-slate-50 rounded-sm p-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-sm text-slate-900">{qtn.quotation_number}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${qtnStatusStyles[qtn.status] || 'bg-slate-100 text-slate-600'}`}>{qtn.status}</span>
                            {idx === 0 && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-xs font-medium">Latest</span>}
                          </div>
                          <span className="text-xs text-slate-400">{formatDate(qtn.created_at)}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs mt-2">
                          <div><span className="text-slate-500 block">Subtotal</span><span className="text-slate-900 font-medium">{qtn.subtotal?.toFixed(2)}</span></div>
                          <div><span className="text-slate-500 block">Tax</span><span className="text-slate-900 font-medium">{qtn.tax_total?.toFixed(2)}</span></div>
                          <div><span className="text-slate-500 block">Grand Total</span><span className="text-slate-900 font-bold">{qtn.grand_total?.toFixed(2)}</span></div>
                        </div>
                        {(qtn.line_items || []).length > 0 && (
                          <div className="mt-2 pt-2 border-t border-slate-200">
                            <div className="text-xs text-slate-500 space-y-0.5">
                              {qtn.line_items.map((li, liIdx) => (
                                <div key={liIdx} className="flex justify-between">
                                  <span>{li.service_item || '-'}</span>
                                  <span className="font-medium text-slate-700">{li.total?.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Activity Timeline */}
        <div className="bg-white rounded-sm border border-slate-200 p-6">
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Activity Timeline</h3>
          {(!lead.activity || lead.activity.length === 0) ? (
            <p className="text-sm text-slate-500">No activity recorded</p>
          ) : (
            <div className="relative">
              <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-slate-200"></div>
              <div className="space-y-4">
                {[...(lead.activity || [])].reverse().map((entry, idx) => (
                  <div key={idx} className="relative pl-9" data-testid={`activity-${idx}`}>
                    <div className={`absolute left-1.5 top-1.5 w-3 h-3 rounded-full ${
                      entry.action === 'Lead Created' ? 'bg-green-500' :
                      entry.action === 'Status Changed' ? (STATUS_DOT[entry.to_status] || 'bg-orange-500') :
                      entry.action === 'Image Uploaded' ? 'bg-indigo-500' :
                      'bg-slate-400'
                    }`}></div>
                    <div className="bg-slate-50 rounded-sm p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-slate-900">{entry.action}</span>
                        <span className="text-xs text-slate-400">{formatDateTime(entry.timestamp)}</span>
                      </div>
                      {entry.from_status && entry.to_status && (
                        <p className="text-xs text-slate-600">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[entry.from_status]?.split(' ').slice(0, 2).join(' ') || 'bg-slate-100 text-slate-600'}`}>{entry.from_status}</span>
                          <span className="mx-1.5">&rarr;</span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[entry.to_status]?.split(' ').slice(0, 2).join(' ') || 'bg-slate-100 text-slate-600'}`}>{entry.to_status}</span>
                        </p>
                      )}
                      {entry.details && <p className="text-xs text-slate-500 mt-1">{entry.details}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ===== MODALS ===== */}

      {/* Received Modal */}
      {activeModal === 'received' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Mark as Received</h2>
              <button onClick={() => setActiveModal(null)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleReceivedSubmit} className="p-6">
              {modalError && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4"><AlertCircle className="h-5 w-5" /><span>{modalError}</span></div>}
              <div className="space-y-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Issue</label>
                  <textarea value={receivedForm.issue} onChange={e => setReceivedForm(p => ({ ...p, issue: e.target.value }))} rows={2} data-testid="received-issue"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" placeholder="Describe the issue..." /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Receivables</label>
                  {receivablesMaster.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 mt-1" data-testid="received-receivables">
                      {receivablesMaster.map(r => (
                        <label key={r.id} className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-sm hover:bg-slate-50 cursor-pointer">
                          <input type="checkbox" checked={receivedForm.receivables.includes(r.name)}
                            onChange={(e) => {
                              setReceivedForm(p => ({
                                ...p,
                                receivables: e.target.checked
                                  ? [...p.receivables, r.name]
                                  : p.receivables.filter(n => n !== r.name)
                              }));
                            }}
                            className="h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500" />
                          <span className="text-sm text-slate-700">{r.name}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic">No receivables configured. Add them in Services Hub &gt; Receivables.</p>
                  )}
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Remark</label>
                  <textarea value={receivedForm.remark} onChange={e => setReceivedForm(p => ({ ...p, remark: e.target.value }))} rows={2} data-testid="received-remark"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                  <textarea value={receivedForm.notes} onChange={e => setReceivedForm(p => ({ ...p, notes: e.target.value }))} rows={2} data-testid="received-notes"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" /></div>
              </div>
              <div className="flex gap-3 justify-end pt-6 mt-4 border-t border-slate-200">
                <button type="button" onClick={() => setActiveModal(null)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={modalSubmitting} data-testid="received-submit"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {modalSubmitting && <Loader2 className="h-4 w-4 animate-spin" />} Mark Received
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Under Inspection Modal */}
      {activeModal === 'under-inspection' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Assign &amp; Move to Inspection</h2>
              <button onClick={() => setActiveModal(null)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleUnderInspection} className="p-6">
              {modalError && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4"><AlertCircle className="h-5 w-5" /><span>{modalError}</span></div>}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Assign To <span className="text-red-500">*</span></label>
                <select value={inspectionAssignee} onChange={e => setInspectionAssignee(e.target.value)} data-testid="inspection-assignee"
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                  <option value="">-- Select Service Manager / Technician --</option>
                  {serviceUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role_name})</option>
                  ))}
                </select>
                {serviceUsers.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No Service Manager or Service Technician users found. Add them in Org Data &gt; Users.</p>
                )}
              </div>
              <div className="flex gap-3 justify-end pt-6 mt-4 border-t border-slate-200">
                <button type="button" onClick={() => setActiveModal(null)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={modalSubmitting || !inspectionAssignee} data-testid="inspection-assign-submit"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {modalSubmitting && <Loader2 className="h-4 w-4 animate-spin" />} Assign &amp; Move
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Inspection Complete Modal */}
      {activeModal === 'inspection-complete' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Inspection Complete — Service Items</h2>
              <button onClick={() => setActiveModal(null)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleInspectionSubmit} className="p-6">
              {modalError && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4"><AlertCircle className="h-5 w-5" /><span>{modalError}</span></div>}
              <div className="space-y-3">
                {inspectionItems.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-3 items-start">
                    <div className="col-span-4">
                      {idx === 0 && <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Item</label>}
                      <select value={item.item_id} onChange={e => { const n = [...inspectionItems]; n[idx] = { ...n[idx], item_id: e.target.value }; setInspectionItems(n); }}
                        data-testid={`insp-item-${idx}`}
                        className="w-full px-2 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white">
                        <option value="">-- Select Item --</option>
                        {serviceItemsMaster.map(si => <option key={si.id} value={si.id}>{si.name}</option>)}
                      </select>
                    </div>
                    <div className="col-span-4">
                      {idx === 0 && <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Description</label>}
                      <input type="text" value={item.description} onChange={e => { const n = [...inspectionItems]; n[idx] = { ...n[idx], description: e.target.value }; setInspectionItems(n); }}
                        data-testid={`insp-desc-${idx}`}
                        className="w-full px-2 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                    </div>
                    <div className="col-span-3">
                      {idx === 0 && <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Remarks</label>}
                      <input type="text" value={item.remarks} onChange={e => { const n = [...inspectionItems]; n[idx] = { ...n[idx], remarks: e.target.value }; setInspectionItems(n); }}
                        data-testid={`insp-rem-${idx}`}
                        className="w-full px-2 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                    </div>
                    <div className="col-span-1 flex items-end">
                      {idx === 0 && <label className="block text-xs font-medium text-slate-500 uppercase mb-1">&nbsp;</label>}
                      {inspectionItems.length > 1 && <button type="button" onClick={() => setInspectionItems(p => p.filter((_, i) => i !== idx))} className="p-2 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setInspectionItems(p => [...p, { item_id: '', description: '', remarks: '' }])} data-testid="insp-add-more"
                className="inline-flex items-center gap-1.5 text-orange-600 hover:text-orange-700 text-sm font-medium mt-3"><Plus className="h-4 w-4" /> Add More</button>

              <div className="mt-4 pt-4 border-t border-slate-200">
                <label className="block text-sm font-medium text-slate-700 mb-1">Assign To (Accounts) <span className="text-red-500">*</span></label>
                <select value={inspCompleteAssignee} onChange={e => setInspCompleteAssignee(e.target.value)} data-testid="insp-assignee"
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                  <option value="">-- Select Accounts User --</option>
                  {accountsUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role_name})</option>
                  ))}
                </select>
                {accountsUsers.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No Accounts users found. Add them in Org Data &gt; Users.</p>
                )}
              </div>

              <div className="flex gap-3 justify-end pt-6 mt-4 border-t border-slate-200">
                <button type="button" onClick={() => setActiveModal(null)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={modalSubmitting} data-testid="insp-submit"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {modalSubmitting && <Loader2 className="h-4 w-4 animate-spin" />} Complete Inspection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quotation Sent Modal — Add pricing */}
      {activeModal === 'quotation-sent' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-3xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Add Pricing & Send Quotation</h2>
              <button onClick={() => setActiveModal(null)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleQuotationSubmit} className="p-6">
              {modalError && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4"><AlertCircle className="h-5 w-5" /><span>{modalError}</span></div>}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">Item</th>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-right w-28">Cost</th>
                      <th className="px-3 py-2 text-left w-40">Tax</th>
                      <th className="px-3 py-2 text-right w-28">Total</th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingItems.map((pi, idx) => {
                      const cost = parseFloat(pi.cost) || 0;
                      const taxObj = taxes.find(t => t.id === pi.tax_id);
                      const taxAmt = taxObj ? cost * (taxObj.percentage / 100) : 0;
                      const lineTotal = cost + taxAmt;
                      return (
                      <tr key={idx} className="border-b border-slate-100">
                        <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                        <td className="px-3 py-2">
                          <select value={pi.item_id || ''} onChange={e => { const n = [...pricingItems]; const svc = serviceItemsMaster.find(s => s.id === e.target.value); n[idx] = { ...n[idx], item_id: e.target.value, item_name: svc?.name || '' }; setPricingItems(n); }}
                            data-testid={`price-item-${idx}`}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm bg-white focus:border-orange-500 focus:outline-none">
                            <option value="">-- Select Item --</option>
                            {serviceItemsMaster.map(si => <option key={si.id} value={si.id}>{si.name}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input type="text" value={pi.description || ''} onChange={e => { const n = [...pricingItems]; n[idx] = { ...n[idx], description: e.target.value }; setPricingItems(n); }}
                            data-testid={`price-desc-${idx}`}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" value={pi.cost} onChange={e => { const n = [...pricingItems]; n[idx] = { ...n[idx], cost: e.target.value }; setPricingItems(n); }}
                            data-testid={`price-cost-${idx}`} min="0" step="0.01"
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm text-right focus:border-orange-500 focus:outline-none" />
                        </td>
                        <td className="px-3 py-2">
                          <select value={pi.tax_id || ''} onChange={e => { const n = [...pricingItems]; n[idx] = { ...n[idx], tax_id: e.target.value || null }; setPricingItems(n); }}
                            data-testid={`price-tax-${idx}`}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm bg-white focus:border-orange-500 focus:outline-none">
                            <option value="">No Tax</option>
                            {taxes.filter(t => t.active).map(t => <option key={t.id} value={t.id}>{t.name} ({t.percentage}%)</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-slate-900" data-testid={`price-total-${idx}`}>
                          {lineTotal.toFixed(2)}
                        </td>
                        <td className="px-3 py-2">
                          {pricingItems.length > 1 && (
                            <button type="button" onClick={() => setPricingItems(p => p.filter((_, i) => i !== idx))}
                              className="p-1 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                    {/* Grand Total Row */}
                    {(() => {
                      let sumCost = 0, sumTax = 0, sumTotal = 0;
                      pricingItems.forEach(pi => {
                        const c = parseFloat(pi.cost) || 0;
                        const t = taxes.find(tx => tx.id === pi.tax_id);
                        const ta = t ? c * (t.percentage / 100) : 0;
                        sumCost += c; sumTax += ta; sumTotal += c + ta;
                      });
                      return (
                        <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold text-sm">
                          <td className="px-3 py-3" colSpan={3} data-testid="pricing-grand-label">
                            <span className="text-slate-700 uppercase tracking-wider text-xs">Totals</span>
                          </td>
                          <td className="px-3 py-3 text-right text-slate-900" data-testid="pricing-sum-cost">{sumCost.toFixed(2)}</td>
                          <td className="px-3 py-3 text-right text-slate-600" data-testid="pricing-sum-tax">{sumTax.toFixed(2)}</td>
                          <td className="px-3 py-3 text-right text-slate-900" data-testid="pricing-grand-total">{sumTotal.toFixed(2)}</td>
                          <td></td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
              <button type="button" onClick={() => setPricingItems(p => [...p, { item_id: '', item_name: '', description: '', remarks: '', cost: '', tax_id: '' }])} data-testid="price-add-more"
                className="inline-flex items-center gap-1.5 text-orange-600 hover:text-orange-700 text-sm font-medium mt-3"><Plus className="h-4 w-4" /> Add More</button>
              <div className="flex gap-3 justify-end pt-6 mt-4 border-t border-slate-200">
                <button type="button" onClick={() => setActiveModal(null)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={modalSubmitting} data-testid="quotation-submit"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {modalSubmitting && <Loader2 className="h-4 w-4 animate-spin" />} Send Quotation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approved Modal */}
      {activeModal === 'approved' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Approve Lead</h3>
            {modalError && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4"><AlertCircle className="h-5 w-5" /><span>{modalError}</span></div>}
            <p className="text-slate-600 mb-6">This will approve the lead and automatically create a service ticket. Continue?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setActiveModal(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              <button onClick={handleApprovedSubmit} disabled={modalSubmitting} data-testid="approved-submit"
                className="px-4 py-2 bg-green-600 text-white font-bold rounded-sm hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-2">
                {modalSubmitting && <Loader2 className="h-4 w-4 animate-spin" />} Approve & Create Ticket
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remark Modal */}
      {activeModal === 'remark' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Update Remark</h2>
              <button onClick={() => setActiveModal(null)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6">
              <textarea value={remarkText} onChange={e => setRemarkText(e.target.value)} rows={4} data-testid="remark-input"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none" />
              <div className="flex gap-3 justify-end pt-4">
                <button onClick={() => setActiveModal(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button onClick={handleRemarkUpdate} data-testid="remark-save"
                  className="px-4 py-2 bg-orange-500 text-white font-bold rounded-sm hover:bg-orange-600">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Upload Modal */}
      {activeModal === 'image-upload' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Upload Image</h2>
              <button onClick={() => setActiveModal(null)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {modalError && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm"><AlertCircle className="h-5 w-5" /><span>{modalError}</span></div>}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Image</label>
                <input type="file" accept="image/*" ref={fileInputRef} onChange={e => setImageFile(e.target.files[0])} data-testid="image-file-input"
                  className="w-full text-sm border border-slate-200 rounded-sm file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-orange-50 file:text-orange-600 file:font-medium" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status at Upload</label>
                <select value={imageStatus} onChange={e => setImageStatus(e.target.value)} data-testid="image-status-select"
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                  {LEAD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Caption</label>
                <input type="text" value={imageCaption} onChange={e => setImageCaption(e.target.value)} data-testid="image-caption"
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
              </div>
              <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
                <button onClick={() => setActiveModal(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button onClick={handleImageUpload} disabled={!imageFile || imageUploading} data-testid="image-upload-submit"
                  className="px-4 py-2 bg-orange-500 text-white font-bold rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {imageUploading && <Loader2 className="h-4 w-4 animate-spin" />} Upload
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Standalone Quotation Modal (Create New / Update Last) */}
      {qtnModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-3xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Create Quotation</h2>
              <button onClick={() => setQtnModalOpen(false)} className="p-2 text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleStandaloneQuotationSubmit} className="p-6">
              {qtnError && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-4"><AlertCircle className="h-5 w-5" /><span>{qtnError}</span></div>}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">Item</th>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-right w-28">Cost</th>
                      <th className="px-3 py-2 text-left w-40">Tax</th>
                      <th className="px-3 py-2 text-right w-28">Total</th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {qtnModalItems.map((pi, idx) => {
                      const cost = parseFloat(pi.cost) || 0;
                      const taxObj = taxes.find(t => t.id === pi.tax_id);
                      const taxAmt = taxObj ? cost * (taxObj.percentage / 100) : 0;
                      const lineTotal = cost + taxAmt;
                      return (
                      <tr key={idx} className="border-b border-slate-100">
                        <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                        <td className="px-3 py-2">
                          <select value={pi.item_id || ''} onChange={e => { const n = [...qtnModalItems]; const svc = serviceItemsMaster.find(s => s.id === e.target.value); n[idx] = { ...n[idx], item_id: e.target.value, item_name: svc?.name || '' }; setQtnModalItems(n); }}
                            data-testid={`qtn-item-${idx}`}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm bg-white focus:border-orange-500 focus:outline-none">
                            <option value="">-- Select Item --</option>
                            {serviceItemsMaster.map(si => <option key={si.id} value={si.id}>{si.name}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input type="text" value={pi.description || ''} onChange={e => { const n = [...qtnModalItems]; n[idx] = { ...n[idx], description: e.target.value }; setQtnModalItems(n); }}
                            data-testid={`qtn-desc-${idx}`}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" value={pi.cost} onChange={e => { const n = [...qtnModalItems]; n[idx] = { ...n[idx], cost: e.target.value }; setQtnModalItems(n); }}
                            data-testid={`qtn-cost-${idx}`} min="0" step="0.01"
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm text-right focus:border-orange-500 focus:outline-none" />
                        </td>
                        <td className="px-3 py-2">
                          <select value={pi.tax_id || ''} onChange={e => { const n = [...qtnModalItems]; n[idx] = { ...n[idx], tax_id: e.target.value || null }; setQtnModalItems(n); }}
                            data-testid={`qtn-tax-${idx}`}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm bg-white focus:border-orange-500 focus:outline-none">
                            <option value="">No Tax</option>
                            {taxes.filter(t => t.active).map(t => <option key={t.id} value={t.id}>{t.name} ({t.percentage}%)</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-slate-900" data-testid={`qtn-total-${idx}`}>
                          {lineTotal.toFixed(2)}
                        </td>
                        <td className="px-3 py-2">
                          {qtnModalItems.length > 1 && (
                            <button type="button" onClick={() => setQtnModalItems(p => p.filter((_, i) => i !== idx))}
                              className="p-1 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                    {(() => {
                      let sumCost = 0, sumTax = 0, sumTotal = 0;
                      qtnModalItems.forEach(pi => {
                        const c = parseFloat(pi.cost) || 0;
                        const t = taxes.find(tx => tx.id === pi.tax_id);
                        const ta = t ? c * (t.percentage / 100) : 0;
                        sumCost += c; sumTax += ta; sumTotal += c + ta;
                      });
                      return (
                        <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold text-sm">
                          <td className="px-3 py-3" colSpan={3}>
                            <span className="text-slate-700 uppercase tracking-wider text-xs">Totals</span>
                          </td>
                          <td className="px-3 py-3 text-right text-slate-900" data-testid="qtn-sum-cost">{sumCost.toFixed(2)}</td>
                          <td className="px-3 py-3 text-right text-slate-600" data-testid="qtn-sum-tax">{sumTax.toFixed(2)}</td>
                          <td className="px-3 py-3 text-right text-slate-900" data-testid="qtn-grand-total">{sumTotal.toFixed(2)}</td>
                          <td></td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
              <button type="button" onClick={() => setQtnModalItems(p => [...p, { item_id: '', item_name: '', description: '', cost: '', tax_id: '' }])} data-testid="qtn-add-more"
                className="inline-flex items-center gap-1.5 text-orange-600 hover:text-orange-700 text-sm font-medium mt-3"><Plus className="h-4 w-4" /> Add More</button>
              <div className="flex gap-3 justify-end pt-6 mt-4 border-t border-slate-200">
                <button type="button" onClick={() => setQtnModalOpen(false)} className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={qtnSubmitting} data-testid="qtn-modal-submit"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {qtnSubmitting && <Loader2 className="h-4 w-4 animate-spin" />} Create Quotation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
